/**
 * **A apresentação contínua do mundo aberto** (gate V28I.BX.1).
 *
 * Tudo o que está aqui é apresentação e mais nada. Nenhuma função decide dano,
 * colisão, alvo, impacto, velocidade, cadência ou regra: elas dizem ONDE
 * desenhar o que a sala já disse, e QUANDO. Puras e sem Phaser, para poderem
 * ser provadas sem uma tela.
 *
 * ## O problema medido
 *
 * Na janela real, com a nave a andar e a disparar, a cena pousava cada corpo
 * na posição do último tique recebido e o casco perseguia um alvo que saltava:
 * os tiros ficavam parados 77% dos quadros mesmo com fotografias a ~15 Hz, e
 * saltavam 62 ou 124 px de cada vez. A câmara perseguia o casco perseguido, e
 * herdava os dois atrasos.
 *
 * ## O que isto faz
 *
 * Um **cursor de apresentação**, em tiques fracionários, ancorado ao `k` das
 * fotografias. Ele anda à velocidade do relógio de parede e é puxado — com um
 * limite de dez por cento — para ficar um pouco ATRÁS da fotografia mais
 * recente. Tudo o que se move desenha-se nesse instante: o casco entre as duas
 * amostras que o rodeiam, o corpo linear pela aritmética da sala. Assim cada
 * quadro avança um bocadinho, em vez de três quadros parados e um salto.
 *
 * ## O que isto NÃO é
 *
 * Não é um relógio da simulação: não corre tique nenhum, não prevê e não
 * extrapola para lá da fotografia mais recente. Se as fotografias pararem, o
 * cursor para na última — congela como a ligação congela, que é o que
 * aconteceria a um corpo de verdade sem ninguém a dizer onde ele está.
 *
 * O PRÓPRIO casco é a exceção, e tem a sua secção no fim (gate V28I.BX.2): é
 * previsto a partir do que o jogador está a segurar, porque esperar pela sala
 * para mostrar a resposta a uma tecla é o atraso que o dono recusou.
 */

import { VECTOR_DIAGONAL, VECTOR_SCALE } from '@kenocyber/game-domain';

/* -------------------------------------------------------------------------- */
/*  O aro de aliado                                                           */
/* -------------------------------------------------------------------------- */

/**
 * **O aro de aliado nunca se desenha no PRÓPRIO casco.**
 *
 * A sala manda sempre o casco de quem recebe a trama com `al: 1` — é aliado de
 * si próprio, por definição — e a cena desenhava-lhe o aro. O resultado era um
 * círculo cinzento permanente à volta da nave, mesmo a solo, que o dono mandou
 * retirar. O aro existe para distinguir um aliado de um inimigo, e ninguém
 * precisa de se distinguir a si próprio: a câmara já está centrada nele.
 *
 * Nos OUTROS aliados continua a desenhar-se: é a única marca que os separa de
 * quem dispara contra nós.
 */
export function openWorldDrawsAllyRing(
  entity: { readonly id: number; readonly ally: boolean },
  meId: number,
): boolean {
  return entity.ally && entity.id !== meId;
}

/* -------------------------------------------------------------------------- */
/*  O zoom do jogador                                                          */
/* -------------------------------------------------------------------------- */

/**
 * **O afastamento máximo: 0,3125 da vista canónica** (gates V28I.BX.2 e
 * V28I.BX.2.1).
 *
 * O gate V28I.BX.2 pôs o chão em 0,5; o dono pediu 60 % mais distância do que
 * isso, e `0,5 / 1,6 = 0,3125`. Numa janela 1920 x 1080 mostra 6144 x 3456 —
 * exatamente a área visível máxima de `viewport.ts`, cuja diagonal inteira a
 * área de interesse do servidor cobre. Numa janela maior quem manda é essa área
 * (`openWorldEnvelopeZoomFor`): a cena nunca mostra mais do que ela, e a roda
 * não se afasta para lá do chão dela. O dente, a transição e o máximo são os
 * de sempre: só o chão desceu.
 */
export const OPEN_WORLD_PLAYER_ZOOM_MIN = 0.3125;
/** A vista canónica: onde uma expedição começa, e para onde um delta sem sentido volta. */
export const OPEN_WORLD_PLAYER_ZOOM_CANONICAL = 1;
/** Até ao dobro. */
export const OPEN_WORLD_PLAYER_ZOOM_MAX = 2;
/** Um dente da roda, como multiplicador: cada dente parece igual aos outros. */
export const OPEN_WORLD_PLAYER_ZOOM_STEP = 1.1;
/** A constante de tempo da transição, em milissegundos: suave, sem degraus. */
export const OPEN_WORLD_PLAYER_ZOOM_EASE_MS = 90;

export function clampOpenWorldPlayerZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) {
    /* Um delta sem sentido nunca apaga a câmara: volta à vista canónica. */
    return OPEN_WORLD_PLAYER_ZOOM_CANONICAL;
  }
  return Math.min(OPEN_WORLD_PLAYER_ZOOM_MAX, Math.max(OPEN_WORLD_PLAYER_ZOOM_MIN, zoom));
}

/** Um dente da roda. `deltaY` positivo (rodar para baixo) afasta. */
export function openWorldPlayerZoomAfterWheel(current: number, deltaY: number): number {
  if (deltaY === 0 || !Number.isFinite(deltaY)) {
    return clampOpenWorldPlayerZoom(current);
  }
  return clampOpenWorldPlayerZoom(
    deltaY > 0 ? current / OPEN_WORLD_PLAYER_ZOOM_STEP : current * OPEN_WORLD_PLAYER_ZOOM_STEP,
  );
}

/**
 * **O zoom mostrado aproxima-se do escolhido pelo tempo, e não por quadro.**
 *
 * Uma fração fixa por quadro faria a transição mais rápida num monitor mais
 * rápido. Esta depende dos milissegundos passados, e chega exatamente ao alvo
 * sem o ultrapassar.
 */
export function easeOpenWorldPlayerZoom(shown: number, target: number, deltaMs: number): number {
  const goal = clampOpenWorldPlayerZoom(target);
  if (!Number.isFinite(shown)) {
    return goal;
  }
  const elapsed = Number.isFinite(deltaMs) ? Math.max(0, deltaMs) : 0;
  const next = shown + (goal - shown) * (1 - Math.exp(-elapsed / OPEN_WORLD_PLAYER_ZOOM_EASE_MS));
  return Math.abs(goal - next) < 1e-4 ? goal : next;
}

/* -------------------------------------------------------------------------- */
/*  O cursor de apresentação                                                   */
/* -------------------------------------------------------------------------- */

/** O atraso de partida, em tiques: 75 ms a 50 ms por tique. */
export const OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS = 1.5;
/**
 * **O atraso máximo, em tiques: 150 ms.**
 *
 * O teto é o que impede este mecanismo de trocar solavancos por atraso de
 * comando. Fotografias mais espaçadas do que isto não se tornam contínuas
 * aqui: o cursor para na mais recente e espera. A cadência das fotografias é
 * da sala, e não se resolve por apresentação.
 */
export const OPEN_WORLD_PRESENTATION_MAX_DELAY_TICKS = 3;
/**
 * **Quanto o presente dos corpos lineares pode ir além da última fotografia**,
 * em tiques. Com fotografias a cada tique, um tique e meio cobre o intervalo e
 * o seu atraso sem deixar um corpo retirado a voar mais do que isso.
 */
export const OPEN_WORLD_PRESENTATION_HEAD_MAX_TICKS = 1.5;
/** Quantos tiques atrás da fotografia mais recente o cursor pode andar. */
export const OPEN_WORLD_PRESENTATION_HISTORY_TICKS = 12;
/**
 * **Um salto maior do que isto não é movimento: é outra situação.**
 *
 * Dois segundos. Uma reconexão, uma correção, uma troca de mundo: o cursor
 * recomeça na fotografia nova em vez de deslizar por um buraco.
 */
export const OPEN_WORLD_PRESENTATION_RESET_GAP_TICKS = 40;

/** O puxão máximo, como fração da velocidade: nunca acelera nem trava mais do que isto. */
const STEER_LIMIT = 0.1;
/** Em quantos tiques um desvio é corrigido. */
const CORRECTION_TICKS = 4;
/** Atrás disto o cursor não rasteja: salta para onde devia estar. */
const CATCH_UP_TICKS = 8;
/** A folga que se junta ao atraso quando uma fotografia chega tarde. */
const DELAY_MARGIN_TICKS = 0.25;
/** Quanto o atraso pedido desce por tique quando nada chega tarde. */
const DELAY_DECAY_PER_TICK = 0.01;
/** O atraso cresce depressa e desce devagar. */
const DELAY_GROW_PER_TICK = 0.25;
const DELAY_SHRINK_PER_TICK = 0.02;

export type OpenWorldCursorObservation = 'first' | 'advanced' | 'same' | 'reset';

/**
 * **Onde, no tempo da sala, se está a desenhar agora.**
 *
 * `observe` recebe cada fotografia (o `k` e o instante em que chegou);
 * `advance` é chamado uma vez por quadro e devolve o tique fracionário a
 * desenhar. Os dois instantes vêm do mesmo relógio de parede do navegador.
 */
export class OpenWorldPresentationCursor {
  private newest: number | null = null;
  private newestAt = 0;
  private cursorTick: number | null = null;
  private lastAt: number | null = null;
  private delayTicks = OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS;
  private requiredTicks = OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS;
  private starvedNow = false;

  constructor(private readonly tickMs: number) {
    if (!Number.isFinite(tickMs) || tickMs <= 0) {
      throw new RangeError('the presentation cursor needs a positive tick length');
    }
  }

  /** Uma fotografia chegou. Diz se ela continua o movimento ou recomeça tudo. */
  observe(tick: number, at: number): OpenWorldCursorObservation {
    if (this.newest === null) {
      this.snap(tick, at);
      return 'first';
    }
    if (tick === this.newest) {
      return 'same';
    }
    if (tick < this.newest || tick - this.newest > OPEN_WORLD_PRESENTATION_RESET_GAP_TICKS) {
      this.snap(tick, at);
      return 'reset';
    }
    this.newest = tick;
    this.newestAt = at;
    return 'advanced';
  }

  /** Um quadro. Devolve o tique a desenhar, ou `null` antes da primeira fotografia. */
  advance(now: number): number | null {
    const newest = this.newest;
    const current = this.cursorTick;
    if (newest === null || current === null) {
      return null;
    }
    const previous = this.lastAt;
    this.lastAt = now;
    const elapsedTicks = previous === null ? 0 : Math.max(0, now - previous) / this.tickMs;
    const wanted = newest + Math.max(0, now - this.newestAt) / this.tickMs - this.delayTicks;
    const steer = Math.max(
      -STEER_LIMIT,
      Math.min(STEER_LIMIT, (wanted - current) / CORRECTION_TICKS),
    );
    let raw = current + elapsedTicks * (1 + steer);
    if (wanted - raw > CATCH_UP_TICKS) {
      /* Muito atrás — uma aba escondida, um arranque: salta, não rasteja. */
      raw = wanted;
    }
    this.cursorTick = Math.max(
      newest - OPEN_WORLD_PRESENTATION_HISTORY_TICKS,
      Math.min(raw, newest),
    );

    /*
     * **Faltou atraso?** Só uma ultrapassagem o prova: o cursor chegou à
     * fotografia mais recente e teve de esperar. É a única evidência que
     * justifica atrasar mais — e o teto impede que isso vire atraso de comando.
     */
    const overshoot = raw - newest;
    this.starvedNow = overshoot > 0;
    if (overshoot > 0) {
      this.requiredTicks = Math.min(
        OPEN_WORLD_PRESENTATION_MAX_DELAY_TICKS,
        Math.max(this.requiredTicks, this.delayTicks + overshoot + DELAY_MARGIN_TICKS),
      );
    } else {
      this.requiredTicks = Math.max(
        OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS,
        this.requiredTicks - elapsedTicks * DELAY_DECAY_PER_TICK,
      );
    }
    const change = this.requiredTicks - this.delayTicks;
    this.delayTicks +=
      change > 0
        ? Math.min(change, elapsedTicks * DELAY_GROW_PER_TICK)
        : Math.max(change, -elapsedTicks * DELAY_SHRINK_PER_TICK);
    return this.cursorTick;
  }

  /**
   * **O tique do presente**, para os corpos lineares (gate V28I.BX.2).
   *
   * A fotografia mais recente, levada pelo relógio de parede até agora, com um
   * teto. Um corpo linear tem a trajetória inteira na base — `base + v x
   * (tique - tique_da_base)` — e é desenhado aqui, e não no cursor atrasado:
   * era o cursor que fazia um tiro acabado de nascer esperar à boca do canhão
   * até o cursor chegar ao tique dele, e esse monte parado e somado em ADD era
   * a "aparência clara e travada" que o dono via ao lado dos tiros que já
   * voavam. Assim nasce já a voar, e do casco que também é desenhado no
   * presente.
   *
   * O teto limita o que se desenha para lá da última fotografia: um corpo que a
   * sala já retirou continua no ecrã, no máximo, esse tanto, até a remoção
   * chegar e o tirar no mesmo quadro.
   */
  head(now: number): number | null {
    if (this.newest === null) {
      return null;
    }
    return (
      this.newest +
      Math.min(
        OPEN_WORLD_PRESENTATION_HEAD_MAX_TICKS,
        Math.max(0, now - this.newestAt) / this.tickMs,
      )
    );
  }

  /** Esquece tudo. A próxima fotografia é a primeira. */
  reset(): void {
    this.newest = null;
    this.cursorTick = null;
    this.lastAt = null;
    this.delayTicks = OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS;
    this.requiredTicks = OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS;
    this.starvedNow = false;
  }

  get cursor(): number | null {
    return this.cursorTick;
  }

  get delay(): number {
    return this.delayTicks;
  }

  /** O último quadro teve de esperar por uma fotografia. */
  get starved(): boolean {
    return this.starvedNow;
  }

  private snap(tick: number, at: number): void {
    this.newest = tick;
    this.newestAt = at;
    this.cursorTick = tick;
    this.lastAt = null;
    this.delayTicks = OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS;
    this.requiredTicks = OPEN_WORLD_PRESENTATION_BASE_DELAY_TICKS;
    this.starvedNow = false;
  }
}

/* -------------------------------------------------------------------------- */
/*  A trilha de um casco                                                       */
/* -------------------------------------------------------------------------- */

/** Quantas amostras uma trilha guarda. Fixo: a trilha nunca cresce. */
export const OPEN_WORLD_TRACK_CAPACITY = 16;
/**
 * **Mais do que isto por tique não é voar: é outra situação.**
 *
 * Duzentas unidades de desenho por tique são quatro mil por segundo, muito
 * acima de qualquer casco. Um renascimento ou uma travessia salta milhares; a
 * trilha recomeça em vez de desenhar a nave a atravessar o mapa.
 */
export const OPEN_WORLD_TELEPORT_UNITS_PER_TICK = 200;

/** Um ponto onde se escreve, para não alocar um objeto por quadro. */
export interface OpenWorldPoint {
  x: number;
  y: number;
}

/**
 * **As últimas posições autoritativas de uma coisa, por tique.**
 *
 * Um anel de tamanho fixo em arrays tipados: empurrar não aloca e amostrar não
 * aloca. Nunca extrapola — antes da primeira amostra devolve a primeira, depois
 * da última devolve a última.
 */
export class OpenWorldTrack {
  private readonly ticks = new Float64Array(OPEN_WORLD_TRACK_CAPACITY);
  private readonly xs = new Float64Array(OPEN_WORLD_TRACK_CAPACITY);
  private readonly ys = new Float64Array(OPEN_WORLD_TRACK_CAPACITY);
  private head = 0;
  private count = 0;

  get size(): number {
    return this.count;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
  }

  /**
   * Uma amostra nova. Devolve `true` quando ela não continuava o movimento e a
   * trilha recomeçou nela.
   */
  push(tick: number, x: number, y: number): boolean {
    let restarted = false;
    if (this.count > 0) {
      const last = this.index(this.count - 1);
      const lastTick = this.ticks[last] ?? 0;
      if (tick < lastTick) {
        return false;
      }
      if (tick === lastTick) {
        this.xs[last] = x;
        this.ys[last] = y;
        return false;
      }
      const reach = OPEN_WORLD_TELEPORT_UNITS_PER_TICK * (tick - lastTick);
      if (Math.hypot(x - (this.xs[last] ?? 0), y - (this.ys[last] ?? 0)) > reach) {
        this.clear();
        restarted = true;
      }
    }
    const slot = this.count === OPEN_WORLD_TRACK_CAPACITY ? this.head : this.index(this.count);
    this.ticks[slot] = tick;
    this.xs[slot] = x;
    this.ys[slot] = y;
    if (this.count === OPEN_WORLD_TRACK_CAPACITY) {
      this.head = (this.head + 1) % OPEN_WORLD_TRACK_CAPACITY;
    } else {
      this.count += 1;
    }
    return restarted;
  }

  /** A posição no tique pedido, escrita em `out`. `false` se a trilha está vazia. */
  sampleAt(tick: number, out: OpenWorldPoint): boolean {
    if (this.count === 0) {
      return false;
    }
    const first = this.index(0);
    const last = this.index(this.count - 1);
    if (tick <= (this.ticks[first] ?? 0)) {
      out.x = this.xs[first] ?? 0;
      out.y = this.ys[first] ?? 0;
      return true;
    }
    if (tick >= (this.ticks[last] ?? 0)) {
      out.x = this.xs[last] ?? 0;
      out.y = this.ys[last] ?? 0;
      return true;
    }
    for (let i = 0; i < this.count - 1; i += 1) {
      const a = this.index(i);
      const b = this.index(i + 1);
      const ta = this.ticks[a] ?? 0;
      const tb = this.ticks[b] ?? 0;
      if (ta <= tick && tick <= tb) {
        const t = tb === ta ? 1 : (tick - ta) / (tb - ta);
        const xa = this.xs[a] ?? 0;
        const ya = this.ys[a] ?? 0;
        out.x = xa + ((this.xs[b] ?? 0) - xa) * t;
        out.y = ya + ((this.ys[b] ?? 0) - ya) * t;
        return true;
      }
    }
    out.x = this.xs[last] ?? 0;
    out.y = this.ys[last] ?? 0;
    return true;
  }

  private index(offset: number): number {
    return (this.head + offset) % OPEN_WORLD_TRACK_CAPACITY;
  }
}

/**
 * **Solta a trilha de quem já não está na fotografia.**
 *
 * Com a aba escondida o Phaser deixa de correr o passo, mas as fotografias
 * continuam a chegar: um corpo que nasce e sai sem nunca ter tido sprite nunca
 * passaria pela libertação que o desenho faz ao retirá-lo, e a trilha dele
 * ficava guardada até uma travessia ou ao fecho. Podar a cada fotografia mantém
 * as trilhas exatamente iguais ao que a sala diz que existe.
 *
 * `release` retira a trilha do mapa; apagar durante a iteração de um `Map` é
 * permitido e não salta ninguém.
 */
export function pruneOpenWorldTracks(
  tracks: ReadonlyMap<number, OpenWorldTrack>,
  seen: ReadonlySet<number>,
  release: (id: number) => void,
): void {
  for (const id of tracks.keys()) {
    if (!seen.has(id)) {
      release(id);
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Um corpo linear                                                            */
/* -------------------------------------------------------------------------- */

/**
 * **Onde um corpo linear está num tique fracionário**, pela aritmética da sala:
 * `base + v x (tique - tique_da_base)`.
 *
 * Nunca antes da base: um corpo cujo nascimento o cursor ainda não alcançou
 * espera no ponto onde nasceu, à boca do canhão desenhado nesse mesmo instante,
 * em vez de ser desenhado para trás, por dentro da nave.
 *
 * Devolve `false`, sem tocar em `out`, quando o corpo não é linear ou a âncora
 * não veio: aí vale a posição que a sala mandou. Recebe a própria `base` e
 * escreve em `out` para a cena não alocar um objeto por corpo por quadro.
 */
export function openWorldLinearShotAt(
  base: {
    readonly linear?: boolean;
    readonly baseX?: number;
    readonly baseY?: number;
    readonly baseTick?: number;
    readonly vx?: number;
    readonly vy?: number;
  },
  tick: number,
  out: OpenWorldPoint,
): boolean {
  if (
    base.linear !== true ||
    base.baseX === undefined ||
    base.baseY === undefined ||
    base.baseTick === undefined ||
    base.vx === undefined ||
    base.vy === undefined
  ) {
    return false;
  }
  const ticks = Math.max(0, tick - base.baseTick);
  out.x = base.baseX + base.vx * ticks;
  out.y = base.baseY + base.vy * ticks;
  return true;
}

/* -------------------------------------------------------------------------- */
/*  O próprio casco                                                            */
/* -------------------------------------------------------------------------- */

/** Quantas mudanças de intenção o diário guarda. Fixo: o diário nunca cresce. */
export const OPEN_WORLD_OWN_HULL_LOG_CAPACITY = 64;
/**
 * **A constante de tempo da correção**, em milissegundos.
 *
 * O erro normal é o arredondamento ao tique da sala — ela só pára um casco numa
 * fronteira de tique, portanto no máximo um tique de viagem — e desfaz-se em
 * três ou quatro quadros, sem degrau.
 */
export const OPEN_WORLD_OWN_HULL_CORRECTION_MS = 25;
/** A correção nunca anda mais depressa do que isto, em múltiplos da velocidade do casco. */
export const OPEN_WORLD_OWN_HULL_MAX_CORRECTION_RATE = 1.5;
/**
 * **Um erro maior do que isto não se desliza: salta.** Em tiques de viagem.
 *
 * Seis tiques são trezentos milissegundos de voo. Nenhum arredondamento chega
 * lá; uma correção da sala, um empurrão, uma colisão ou um renascimento sim, e
 * aí deslizar seria desenhar um caminho que o casco não fez.
 */
export const OPEN_WORLD_OWN_HULL_SNAP_TICKS = 6;
/**
 * **Um alinhamento mais velho do que isto não se usa**, em milissegundos. O
 * batimento da ligação renova-o a cada meio segundo; um valor de um segundo
 * atrás é de uma situação que já passou.
 */
export const OPEN_WORLD_OWN_HULL_SYNC_TRUST_MS = 1_000;
/**
 * **Abaixo disto, em unidades de desenho, o resto da correção sai de uma vez.**
 *
 * Menos de um pixel na vista canónica. Deixá-lo esvair-se devagar era o casco
 * a rastejar frações de pixel durante um quarto de segundo depois de parar —
 * medido na bancada — e um salto deste tamanho não se vê.
 */
const OWN_HULL_REST_UNITS = 0.75;
/** A componente de uma diagonal normalizada: a MESMA constante da sala. */
const DIAGONAL = VECTOR_DIAGONAL / VECTOR_SCALE;
/** Um passo autoritativo só ensina a velocidade se for na direção da intenção. */
const ALIGNED_COSINE = 0.999;
const EDGE_EPSILON = 1e-6;

/** O que uma fotografia diz do próprio casco, em unidades de desenho. */
export interface OpenWorldOwnHullSample {
  readonly tick: number;
  /** A posição autoritativa neste tique. */
  readonly x: number;
  readonly y: number;
  /** A intenção que a sala APLICOU neste tique. */
  readonly moveX: number;
  readonly moveY: number;
  /**
   * **O instante local a que este tique corresponde**, ou `null` antes do
   * primeiro reconhecimento: o envio do último comando que a sala aplicou, mais
   * os tiques que ela já correu com ele. Ver `OpenWorldConnection`.
   */
  readonly syncAt: number | null;
  /** As paredes para o CENTRO deste casco: `[raio, lado - raio]` em cada eixo. */
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  /** Congelado, a pescar: a sala não o deixa andar por vontade própria. */
  readonly immobile: boolean;
}

export type OpenWorldOwnHullObservation = 'first' | 'advanced' | 'same' | 'snap';

/**
 * **Onde desenhar o próprio casco: a sala, levada até agora pelo que o
 * jogador está a segurar** (gate V28I.BX.2).
 *
 * Só apresentação. Não decide colisão, dano, alcance nem regra, e nada disto
 * volta à sala: ela continua a receber a intenção, e só a intenção.
 *
 * ## Como
 *
 * Um **diário** das mudanças de intenção local, com o instante de cada uma. A
 * cada fotografia, a posição autoritativa torna-se a **base**, ancorada ao
 * instante local a que aquele tique corresponde. A posição desenhada é a base
 * mais a integral do diário desde esse instante até agora — para trás também:
 * a sala aplica um tique inteiro de uma vez, e logo depois de um comando ela
 * está À FRENTE do que o jogador já segurou.
 *
 * Assim uma tecla move o casco no quadro seguinte, largá-la pára-o no quadro
 * seguinte, e a sala, quando responde, confirma em vez de corrigir. O que ela
 * corrige — o arredondamento ao tique, outro casco no caminho — desliza para o
 * sítio por um desvio que se esvai, e um desvio grande de mais salta.
 *
 * ## A velocidade
 *
 * Não viaja: é aprendida dos passos autoritativos que seguem a intenção, fora
 * das paredes. Até ao primeiro, vale a de catálogo. Um passo nulo com a
 * intenção a pedir movimento — a carga que prende, outro casco à frente — diz
 * "parado", até a sala voltar a mexê-lo ou a intenção mudar.
 *
 * ## As paredes
 *
 * Nunca desenhado para lá delas: cada troço é preso às paredes do quadrante,
 * como a sala prende cada tique, e o resultado final também.
 */
export class OpenWorldOwnHullPredictor {
  private readonly times = new Float64Array(OPEN_WORLD_OWN_HULL_LOG_CAPACITY);
  private readonly mxs = new Int8Array(OPEN_WORLD_OWN_HULL_LOG_CAPACITY);
  private readonly mys = new Int8Array(OPEN_WORLD_OWN_HULL_LOG_CAPACITY);
  private head = 0;
  private count = 0;

  private hasBase = false;
  private baseTick = 0;
  private baseX = 0;
  private baseY = 0;
  private baseMoveX = 0;
  private baseMoveY = 0;
  private minX = 0;
  private maxX = 0;
  private minY = 0;
  private maxY = 0;
  private immobile = false;
  /** A base levada até ao instante da fotografia: de onde o desenho parte. */
  private anchorX = 0;
  private anchorY = 0;
  private anchorAt = 0;

  private speed: number;
  private stalledNow = false;
  private offsetX = 0;
  private offsetY = 0;
  private offsetAt: number | null = null;
  private snaps = 0;
  private readonly point: OpenWorldPoint = { x: 0, y: 0 };

  constructor(
    private readonly tickMs: number,
    private readonly fallbackUnitsPerTick: number,
  ) {
    if (!Number.isFinite(tickMs) || tickMs <= 0) {
      throw new RangeError('the own-hull predictor needs a positive tick length');
    }
    if (!Number.isFinite(fallbackUnitsPerTick) || fallbackUnitsPerTick <= 0) {
      throw new RangeError('the own-hull predictor needs a positive fallback speed');
    }
    this.speed = fallbackUnitsPerTick;
  }

  /** A intenção local neste instante. Repetir a mesma não escreve nada. */
  steer(at: number, moveX: number, moveY: number): void {
    const mx = Math.sign(moveX);
    const my = Math.sign(moveY);
    let when = at;
    if (this.count > 0) {
      const last = this.index(this.count - 1);
      if (this.mxs[last] === mx && this.mys[last] === my) {
        return;
      }
      when = Math.max(when, this.times[last] ?? when);
    } else if (mx === 0 && my === 0) {
      /* Um diário vazio já diz "parado". */
      return;
    }
    const slot =
      this.count === OPEN_WORLD_OWN_HULL_LOG_CAPACITY ? this.head : this.index(this.count);
    this.times[slot] = when;
    this.mxs[slot] = mx;
    this.mys[slot] = my;
    if (this.count === OPEN_WORLD_OWN_HULL_LOG_CAPACITY) {
      this.head = (this.head + 1) % OPEN_WORLD_OWN_HULL_LOG_CAPACITY;
    } else {
      this.count += 1;
    }
  }

  /** Uma fotografia do próprio casco. `now` é o relógio de apresentação. */
  observe(sample: OpenWorldOwnHullSample, now: number): OpenWorldOwnHullObservation {
    const syncAt =
      sample.syncAt !== null &&
      Number.isFinite(sample.syncAt) &&
      Math.abs(sample.syncAt - now) <= OPEN_WORLD_OWN_HULL_SYNC_TRUST_MS
        ? sample.syncAt
        : now;
    if (!this.hasBase) {
      this.rebase(sample, syncAt, now);
      this.dropOffset(now);
      return 'first';
    }
    const gap = sample.tick - this.baseTick;
    if (gap === 0) {
      return 'same';
    }
    const jump = Math.hypot(sample.x - this.baseX, sample.y - this.baseY);
    if (gap < 0 || jump > OPEN_WORLD_TELEPORT_UNITS_PER_TICK * gap) {
      /* Um renascimento, uma travessia, o tique para trás: outra situação. */
      this.rebase(sample, syncAt, now);
      this.dropOffset(now);
      this.snaps += 1;
      return 'snap';
    }
    /*
     * O que está no ecrã agora, pela âncora e pela velocidade antigas, fica no
     * ecrã: o desvio absorve a diferença. Medido ANTES de aprender, ou um casco
     * que a sala mostrou parado reescrevia para trás o que já estava desenhado.
     */
    this.presentAt(now, this.point);
    const shownX = this.point.x;
    const shownY = this.point.y;
    this.learn(sample, gap);
    this.rebase(sample, syncAt, now);
    this.offsetX = shownX - this.anchorX;
    this.offsetY = shownY - this.anchorY;
    this.offsetAt = now;
    if (Math.hypot(this.offsetX, this.offsetY) > this.reach() * OPEN_WORLD_OWN_HULL_SNAP_TICKS) {
      this.dropOffset(now);
      this.snaps += 1;
      return 'snap';
    }
    return 'advanced';
  }

  /** Onde desenhar agora, escrito em `out`. `false` antes da primeira fotografia. */
  sample(now: number, out: OpenWorldPoint): boolean {
    if (!this.hasBase) {
      return false;
    }
    this.presentAt(now, out);
    return true;
  }

  /** Esquece a base e o desvio. O diário fica: o que está a ser segurado continua. */
  reset(): void {
    this.hasBase = false;
    this.stalledNow = false;
    this.offsetX = 0;
    this.offsetY = 0;
    this.offsetAt = null;
  }

  /** A velocidade aprendida, em unidades de desenho por tique. */
  get unitsPerTick(): number {
    return this.speed;
  }

  /** A sala mostrou o casco parado com a intenção a pedir movimento. */
  get stalled(): boolean {
    return this.stalledNow;
  }

  /** O desvio que ainda falta desfazer, em unidades de desenho. */
  get correction(): number {
    return Math.hypot(this.offsetX, this.offsetY);
  }

  /** Quantas vezes o desenho saltou em vez de deslizar. */
  get snapCount(): number {
    return this.snaps;
  }

  private rebase(sample: OpenWorldOwnHullSample, at: number, now: number): void {
    this.hasBase = true;
    this.baseTick = sample.tick;
    this.baseX = sample.x;
    this.baseY = sample.y;
    this.baseMoveX = Math.sign(sample.moveX);
    this.baseMoveY = Math.sign(sample.moveY);
    this.minX = sample.minX;
    this.maxX = Math.max(sample.minX, sample.maxX);
    this.minY = sample.minY;
    this.maxY = Math.max(sample.minY, sample.maxY);
    this.immobile = sample.immobile;
    /*
     * **A âncora: a base levada até AGORA, uma vez só.** Para trás também — a
     * sala aplica um tique inteiro de uma vez e fica à frente do que o jogador
     * já segurou. Feito aqui, e não a cada quadro: um gesto depois deste
     * instante só mexe no que vem a seguir, e largar uma tecla nunca empurra o
     * casco para a frente.
     */
    this.integrate(sample.x, sample.y, at, now, this.point);
    this.anchorX = this.point.x;
    this.anchorY = this.point.y;
    this.anchorAt = now;
  }

  /** O que um passo autoritativo ensina sobre a velocidade deste casco. */
  private learn(sample: OpenWorldOwnHullSample, gap: number): void {
    const mx = Math.sign(sample.moveX);
    const my = Math.sign(sample.moveY);
    if (mx !== this.baseMoveX || my !== this.baseMoveY) {
      /* Outra direção pode estar livre: o "parado" era da anterior. */
      this.stalledNow = false;
    }
    if (gap !== 1 || (mx === 0 && my === 0)) {
      return;
    }
    const pinned =
      (mx !== 0 &&
        (sample.x <= sample.minX + EDGE_EPSILON || sample.x >= sample.maxX - EDGE_EPSILON)) ||
      (my !== 0 &&
        (sample.y <= sample.minY + EDGE_EPSILON || sample.y >= sample.maxY - EDGE_EPSILON));
    if (pinned) {
      return;
    }
    const dx = sample.x - this.baseX;
    const dy = sample.y - this.baseY;
    const length = Math.hypot(dx, dy);
    if (length === 0) {
      this.stalledNow = true;
      return;
    }
    const f = mx !== 0 && my !== 0 ? DIAGONAL : 1;
    if ((dx * mx * f + dy * my * f) / length < ALIGNED_COSINE) {
      /* Um empurrão, um deslize contra outro casco: não é a velocidade dele. */
      return;
    }
    this.speed = length;
    this.stalledNow = false;
  }

  /**
   * **Um ponto levado pelo diário de `from` até `to`**, escrito em `out`.
   *
   * Para a frente, troço a troço e preso às paredes a cada um, como a sala
   * prende cada tique. Para trás, a desfazer o que a sala já andou à frente.
   */
  private integrate(
    fromX: number,
    fromY: number,
    from: number,
    to: number,
    out: OpenWorldPoint,
  ): void {
    let x = fromX;
    let y = fromY;
    const perMs = this.immobile || this.stalledNow ? 0 : this.speed / this.tickMs;
    if (perMs > 0 && to !== from) {
      const forward = to > from;
      const lo = forward ? from : to;
      const hi = forward ? to : from;
      /*
       * Rebobinar é desfazer o que a sala já andou à frente. Um eixo que ela
       * deixou encostado à parede, a empurrar contra ela, não andou nada.
       */
      const pinnedLowX = fromX <= this.minX + EDGE_EPSILON;
      const pinnedHighX = fromX >= this.maxX - EDGE_EPSILON;
      const pinnedLowY = fromY <= this.minY + EDGE_EPSILON;
      const pinnedHighY = fromY >= this.maxY - EDGE_EPSILON;
      for (let i = 0; i < this.count; i += 1) {
        const slot = this.index(i);
        const mx = this.mxs[slot] ?? 0;
        const my = this.mys[slot] ?? 0;
        if (mx === 0 && my === 0) {
          continue;
        }
        const start = Math.max(lo, this.times[slot] ?? 0);
        const end = Math.min(
          hi,
          i + 1 < this.count ? (this.times[this.index(i + 1)] ?? hi) : Number.POSITIVE_INFINITY,
        );
        if (end <= start) {
          continue;
        }
        const travel = perMs * (end - start) * (mx !== 0 && my !== 0 ? DIAGONAL : 1);
        if (forward) {
          x = clampUnits(x + mx * travel, this.minX, this.maxX);
          y = clampUnits(y + my * travel, this.minY, this.maxY);
        } else {
          if (!((mx < 0 && pinnedLowX) || (mx > 0 && pinnedHighX))) {
            x -= mx * travel;
          }
          if (!((my < 0 && pinnedLowY) || (my > 0 && pinnedHighY))) {
            y -= my * travel;
          }
        }
      }
    }
    out.x = clampUnits(x, this.minX, this.maxX);
    out.y = clampUnits(y, this.minY, this.maxY);
  }

  /** A previsão mais o desvio que ainda falta, preso às paredes. */
  private presentAt(now: number, out: OpenWorldPoint): void {
    this.decay(now);
    this.integrate(this.anchorX, this.anchorY, this.anchorAt, Math.max(now, this.anchorAt), out);
    out.x = clampUnits(out.x + this.offsetX, this.minX, this.maxX);
    out.y = clampUnits(out.y + this.offsetY, this.minY, this.maxY);
  }

  /** O desvio esvai-se pelo tempo, e nunca mais depressa do que o teto. */
  private decay(now: number): void {
    const previous = this.offsetAt;
    this.offsetAt = previous === null ? now : Math.max(previous, now);
    if (previous === null) {
      return;
    }
    const elapsed = Math.max(0, now - previous);
    const length = Math.hypot(this.offsetX, this.offsetY);
    if (length === 0 || elapsed === 0) {
      return;
    }
    const eased = length * Math.exp(-elapsed / OPEN_WORLD_OWN_HULL_CORRECTION_MS);
    const limit = (this.reach() * OPEN_WORLD_OWN_HULL_MAX_CORRECTION_RATE * elapsed) / this.tickMs;
    const next = Math.max(eased, length - limit);
    const kept = next < OWN_HULL_REST_UNITS ? 0 : next / length;
    this.offsetX *= kept;
    this.offsetY *= kept;
  }

  private dropOffset(now: number): void {
    this.offsetX = 0;
    this.offsetY = 0;
    this.offsetAt = now;
  }

  /** Um tique de viagem, nunca abaixo do de catálogo: a escala dos limites. */
  private reach(): number {
    return Math.max(this.speed, this.fallbackUnitsPerTick);
  }

  private index(offset: number): number {
    return (this.head + offset) % OPEN_WORLD_OWN_HULL_LOG_CAPACITY;
  }
}

function clampUnits(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/* -------------------------------------------------------------------------- */
/*  O alinhamento do próprio casco                                             */
/* -------------------------------------------------------------------------- */

/**
 * Quantas sequências por reconhecer se lembram. A sala reconhece a cada tique;
 * isto só enche com o socket mudo, e aí o que se esquece é o mais velho.
 */
export const OPEN_WORLD_ACK_CLOCK_CAPACITY = 256;

/**
 * **A que instante local corresponde cada tique, para o próprio casco**
 * (gate V28I.BX.2).
 *
 * O `a` de cada fotografia é a última sequência que a sala aplicou. Na primeira
 * fotografia que cobre uma transição de movimento, a sala aplicou-a nesse tique
 * — ela escreve a intenção ao receber, e o tique seguinte já a usa — e o casco
 * andou esse tique inteiro com ela. Portanto o tique `k` corresponde ao instante
 * em que essa transição saiu daqui, mais `k - tique_do_reconhecimento + 1`
 * tiques.
 *
 * **Só as transições de movimento alinham.** Cada comando espera pelo tique uma
 * fração diferente de um tique; com a mira a sair a 60 Hz, alinhar pelo último
 * comando qualquer mudava a âncora a cada fotografia por até um tique de
 * viagem, e o casco acelerava e travava 20 vezes por segundo — medido na
 * bancada, o passo por quadro variava 23%. Uma transição é o começo do troço que
 * o casco previsto está a integrar, e é contra ela que a sala se compara.
 *
 * **Uma transição que a sala ainda não aplicou limita o alinhamento**: o tique
 * dela ainda não chegou a esse instante, e o casco previsto não pode tratar o
 * que ela mudou como já feito pela sala.
 *
 * Só carimba; nada disto vai para a sala, e nenhuma decisão da ligação depende
 * disto.
 */
export class OpenWorldAckClock {
  private readonly sentAt = new Map<number, number>();
  private ackTick = 0;
  private ackSentAt: number | null = null;

  constructor(private readonly tickMs: number) {
    if (!Number.isFinite(tickMs) || tickMs <= 0) {
      throw new RangeError('the ack clock needs a positive tick length');
    }
  }

  /** Uma transição de movimento saiu agora, com esta sequência. Limitado: o mapa nunca cresce sem fim. */
  sent(seq: number, at: number): void {
    this.sentAt.set(seq, at);
    if (this.sentAt.size > OPEN_WORLD_ACK_CLOCK_CAPACITY) {
      const oldest = this.sentAt.keys().next();
      if (oldest.done !== true) {
        this.sentAt.delete(oldest.value);
      }
    }
  }

  /**
   * A fotografia do tique `tick` reconhece até `ack`. Devolve o instante local
   * a que esse tique corresponde, ou `null` sem nenhum reconhecimento que se
   * tenha visto sair.
   */
  acknowledged(ack: number, tick: number): number | null {
    /*
     * A transição mais recente que este reconhecimento cobre é a que alinha.
     * Um reconhecimento que não cobre nenhuma — só mira, só batimento, ou uma
     * sequência de antes de um socket novo — deixa o alinhamento que havia.
     */
    let latest: number | undefined;
    for (const [seq, at] of this.sentAt) {
      if (seq > ack) {
        break;
      }
      latest = at;
      this.sentAt.delete(seq);
    }
    if (latest !== undefined) {
      this.ackTick = tick;
      this.ackSentAt = latest;
    }
    if (this.ackSentAt === null) {
      return null;
    }
    const aligned = this.ackSentAt + (tick - this.ackTick + 1) * this.tickMs;
    const pending = this.sentAt.values().next();
    return pending.done === true ? aligned : Math.min(aligned, pending.value);
  }

  /** Um socket novo: a contagem recomeça, e nenhum reconhecimento velho vale nele. */
  reset(): void {
    this.sentAt.clear();
    this.ackTick = 0;
    this.ackSentAt = null;
  }

  /** Quantas sequências esperam reconhecimento. */
  get pending(): number {
    return this.sentAt.size;
  }
}

/* -------------------------------------------------------------------------- */
/*  O foco                                                                     */
/* -------------------------------------------------------------------------- */

/** O mínimo de uma janela e de um documento que isto usa. */
export interface OpenWorldFocusTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/**
 * **Uma janela sem foco não continua a segurar teclas.**
 *
 * Quando o foco sai, o navegador nunca entrega o `keyup` da tecla que estava em
 * baixo — ele vai para a outra janela. A intenção guardava-a, e a nave voava
 * sozinha enquanto o jogador olhava para outra coisa: medido na bancada, a nave
 * atravessou meio quadrante assim.
 *
 * Em `blur` e quando o documento fica oculto, `release` é chamado. Ao voltar o
 * foco não se faz nada: o jogador tem de voltar a premir.
 */
export function releaseOpenWorldIntentOnFocusLoss(
  win: OpenWorldFocusTarget,
  doc: OpenWorldFocusTarget & { readonly visibilityState: string },
  release: () => void,
): () => void {
  const onBlur = (): void => release();
  const onVisibility = (): void => {
    if (doc.visibilityState === 'hidden') {
      release();
    }
  };
  win.addEventListener('blur', onBlur);
  doc.addEventListener('visibilitychange', onVisibility);
  return () => {
    win.removeEventListener('blur', onBlur);
    doc.removeEventListener('visibilitychange', onVisibility);
  };
}
